var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var VitesseRunPipes_1;
/*
 * <vitesse-run-pipes> — the agent tree as a CANVAS of piped nodes.
 *
 * WHY A GRAPH AND NOT ROWS. Indented rows say who spawned whom and nothing else, and "who spawned
 * whom" is the least interesting thing about a running fleet of agents. What a person is actually
 * reading is a PIPELINE: what each node was asked for (its task), what it owes back (its
 * deliverable), whether it is moving, and where its answer goes when it lands. Those are properties
 * of the edge as much as the node, so the edge has to be drawn. A card also has room for the task
 * and the deliverable side by side, which a single indented line does not — and those two lines are
 * the whole reason a node is legible without opening it.
 *
 * The rows view remains, as TRACE: a chronological, scannable list is the better instrument for
 * "what happened, in order", and the canvas is the better one for "what is the shape of this".
 * Two views over one tree, each doing the thing it is good at.
 *
 * LAYOUT IS LAYERED AND DETERMINISTIC — depth ACROSS, spawn order down. A force-directed or
 * prettier tidy-tree layout would move nodes on every poll as siblings appear, and a canvas that
 * rearranges itself while you are reading it is worse than one that is merely not the tightest
 * packing. Same input, same picture, every two seconds.
 *
 * It holds no state but the pan offset, fetches nothing, and reads the same `RunTree` the rows do.
 */
import { LitElement, html, nothing, svg, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { runState, runStateLabel, runLabel, runElapsed, runCost, runTokens } from './run-format';
import { latestAttempt } from './run-tree';
import { zoomAt, zoomBy, fit, panBy, viewportRect, centreOn, lerpCamera, easeInOutCubic, framedOn } from '../canvas/camera';
import { statusBadge, statusBadgeStyles } from '../components/templates';
import { iconButton } from '../elements/shared';
import '../elements/vitesse-icon';
/** Card geometry. Fixed so the layout is arithmetic rather than a measure-then-place pass, which
 *  would reflow on every poll and defeat the point of a deterministic layout. */
const CARD_W = 210;
const CARD_H = 104;
const GAP_X = 26;
const GAP_Y = 54;
const PAD = 28;
/** How close "in" is. Enough that the card and its status read without squinting, not so close that
 *  its neighbours leave the frame — arriving with no sense of what is around it defeats the journey.
 *  1.6 was past that line: the destination filled the canvas and the agent beside it was gone. */
const FOCUS_ZOOM = 1.25;
/**
 * ALL THE WAY IN — the last leg, and the only one whose destination is not this surface.
 *
 * FOCUS_ZOOM is a FRAMING: it answers "which card, and what is around it", and 1.25 is right for
 * that because the neighbours stay in shot. The final leg is doing a different job — it is the
 * movement that hands the reader over to the thread — so stopping at a framing left the card sitting
 * politely in the middle of a canvas and then cut to a chat, which reads as two separate events
 * rather than one. Multiplied out from the CARD, so the card covers the stage whatever shape the
 * rail has been dragged to; `clampZoom` caps it, and at the cap the card still fills most of the
 * frame, which is enough for the swap to land as arrival.
 */
const enterZoom = (contW, contH) => Math.max(contW / CARD_W, contH / CARD_H);
/*
 * ALL THE WAY OUT — the framing a journey pulls back to, and the one it starts from in reverse.
 *
 * The card, and TWO CARD-WIDTHS OF ROOM ON EVERY SIDE. That is the whole rule: it is far enough
 * back that the card's neighbours, its parent and the edges it hangs off are all in shot, so the
 * moment you leave a thread you can see where that agent sat; and it is close enough that the card
 * is still plainly a card rather than a tile in a field of them.
 *
 * Stated as a MULTIPLE OF THE CARD rather than as a fit of the whole forest, because the forest is
 * whatever it happens to be — a two-agent tree and a forty-agent one framed by their own extent
 * gave two completely different senses of scale for the same movement. This one is the same
 * movement every time.
 *
 * `Math.min` of the two axes is what respects the viewport: on a narrow rail the width decides, on
 * a short one the height does, and neither is allowed to crop the room the other asked for.
 */
const REVEAL_CARDS = 5; // the card itself, plus two of its own widths either side
const revealZoom = (contW, contH) => Math.min(contW / (CARD_W * REVEAL_CARDS), contH / (CARD_H * REVEAL_CARDS));
/*
 * THE TWO HALVES OF THE MOVEMENT, and they are the same length on purpose.
 *
 * Going in and coming out are one relationship seen from two sides, so they take the same time; a
 * dive that was shorter than the pull-back made the pair read as two different animations. The
 * crossing between two cards is shorter than either, because it is not a place you stop — it is the
 * part of the arc that passes over the tree on the way to the card you asked for.
 */
const FLIGHT_MS = 260;
const CROSS_MS = 180;
/** What a leg is worth when it does not say — only reached by a caller that omits `ms`. */
const LEG_MS = FLIGHT_MS;
/**
 * How much zoom one scrolled pixel is worth.
 *
 * ~8% per full mouse detent (100px), and under 1% for a single trackpad frame — the two devices
 * report wildly different event rates for the same intent, and this is what makes them agree.
 */
const ZOOM_PER_PIXEL = 0.0008;
/** A wheel event's zoom factor, normalised across the three delta modes.
 *
 *  A browser may report a wheel in pixels, LINES or PAGES, and the same gesture in line-mode reports
 *  numbers around 3 where pixel-mode reports 100. Left unconverted, zoom is ~30× slower on whichever
 *  browser chose the other unit — the kind of difference that reads as a broken trackpad. */
export function wheelZoom(ev) {
    const px = ev.deltaMode === 1 ? ev.deltaY * 16 : ev.deltaMode === 2 ? ev.deltaY * 400 : ev.deltaY;
    // Bounded per event so one enormous delta — a flung trackpad, a synthetic event — cannot jump the
    // whole zoom range in a single frame.
    return Math.min(1.25, Math.max(1 / 1.25, Math.exp(-px * ZOOM_PER_PIXEL)));
}
/**
 * THE MINIMAP BOX — a fixed landscape rectangle, exactly the flow canvas's.
 *
 * It was briefly shaped to match the stage, on the reasoning that a map should have the proportions
 * of the thing it maps. In a drag-resizable rail that produced a near-square box, and then a
 * portrait one, and a HUD that changes shape as you drag its own panel is a control that will not
 * sit still. The map's job is done by the FRAME inside it, which is the stage in graph units and
 * therefore already carries the viewport's shape and size at every zoom.
 *
 * So: the box is furniture and stays put; the rectangle inside it is the instrument.
 */
const MM_W = 190;
const MM_H = 126;
/**
 * What one agent spent over every attempt it made. Usage is per run, so a woken agent's card read
 * only its last wake's few hundred tokens and hid the fourteen minutes of work before it. The
 * subtree figure is the first attempt's: the children hang off that run.
 */
function sumUsage(runs) {
    let costUsd = 0;
    let inputTokens = 0n;
    let outputTokens = 0n;
    let any = false;
    for (const r of runs) {
        if (!r.usage)
            continue;
        any = true;
        costUsd += r.usage.costUsd ?? 0;
        inputTokens += r.usage.inputTokens ?? 0n;
        outputTokens += r.usage.outputTokens ?? 0n;
    }
    return { usage: any ? { ...runs[0].usage, costUsd, inputTokens, outputTokens } : undefined, subtreeUsage: runs[0].subtreeUsage };
}
let VitesseRunPipes = class VitesseRunPipes extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * THE NODE THE READER WAS JUST INSIDE — where the journey begins.
         *
         * The canvas is reached from a thread, and landing on a framed forest gives no sense of which of
         * those cards you were just reading. So it OPENS on that card, close in, and pulls back: the
         * first half of the same journey a click across the tree plays, on its own. That half is the one
         * that answers "where am I", and it is the half every entry to this surface wants.
         *
         * '' is a legitimate value — the conversation card — so `hasFrom` is what says whether there is
         * one at all.
         */
        this.fromRunId = '';
        this.hasFrom = false;
        /* The camera, as ONE value rather than three loose numbers. The arithmetic lives in
           canvas/camera — the same functions the flow canvas and the fleet map want, which is why it is
           shared rather than copied a fourth time. */
        this.zoom = 1;
        /** Set once the reader moves the camera themselves: the graph auto-frames while it streams in,
         *  and must stop the moment somebody has chosen a view, or it yanks the ground from under them. */
        this.userMoved = false;
        /** The stage's measured size, for fit and for the minimap's viewport rect. */
        this.contW = 0;
        this.contH = 0;
        this.panX = 0;
        this.panY = 0;
        this.dragging = false;
        this.lastX = 0;
        this.lastY = 0;
        /** Distinguishes a pan from a click: a drag that moved is not a selection. */
        this.moved = false;
        /**
         * THE READER TOOK THE WHEEL — panned, zoomed or scrubbed by hand.
         *
         * Distinct from `userMoved`, which a FLIGHT also sets (a chosen framing is a framing, and auto-fit
         * must leave it alone). This one is only ever set by a hand on the canvas, and it is what decides
         * whether arriving at an agent should still step into it: a journey the reader let run is a
         * journey they asked for, and one they grabbed halfway is not.
         */
        this.grabbed = false;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
        this.onChange = () => this.requestUpdate();
        this.ro = null;
        this.observed = null;
        this.settle = null;
        /** The run already flown to, so arriving does not re-fly on every poll tick. */
        this.flownTo = '';
        this.flying = 0;
        /**
         * ZOOM THAT ANSWERS TO HOW FAR YOU SCROLLED, not to how many events arrived.
         *
         * It used to apply a flat ×1.12 per wheel event regardless of magnitude. A mouse wheel sends one
         * event per detent and that reads fine; a trackpad sends a stream of small ones for a single
         * two-finger swipe, and each was worth a whole detent — so the graph shot from fitted to fully
         * zoomed before the gesture finished, and getting back to a readable scale meant nudging it in
         * the other direction and overshooting again.
         *
         * Proportional to the distance instead, through an exponential so that scrolling the same
         * distance twice multiplies the zoom by the same factor twice — which is what makes zoom feel
         * even rather than crawling at the low end and lurching at the high one. The per-pixel constant
         * is deliberately gentle: a full mouse detent (~100px) is about 8%, where it was 12%, and a
         * single trackpad frame is under 1%.
         */
        this.onWheel = (ev) => {
            ev.preventDefault();
            const r = ev.currentTarget.getBoundingClientRect();
            this.userMoved = true;
            this.grabbed = true;
            this.apply(zoomAt({ zoom: this.zoom, panX: this.panX, panY: this.panY }, ev.clientX - r.left, ev.clientY - r.top, wheelZoom(ev)));
        };
        /*
         * CAPTURE ONLY ONCE A DRAG IS REAL.
         *
         * Capturing on pointerdown retargets every later pointer event to the stage, and the browser
         * synthesises `click` from that retargeted pair — so the card underneath never received one and
         * a node could not be stepped into at all. The canvas panned perfectly and was completely inert
         * to clicking, which looks like a dead card rather than a capture bug.
         *
         * So the pointer is captured on the first movement past the threshold, by which point the
         * gesture IS a pan and swallowing the click is exactly what should happen. A press-and-release
         * that never moves is left entirely alone and reaches the button as an ordinary click.
         */
        this.down = (e) => {
            this.dragging = true;
            this.moved = false;
            this.lastX = e.clientX;
            this.lastY = e.clientY;
            this.requestUpdate();
        };
        this.move = (e) => {
            if (!this.dragging)
                return;
            const dx = e.clientX - this.lastX;
            const dy = e.clientY - this.lastY;
            // A few pixels of travel is a click with a shaky hand, not a pan.
            if (!this.moved && Math.abs(dx) + Math.abs(dy) > 3) {
                this.moved = true;
                // NOW it is a pan: take the pointer so leaving the stage mid-drag does not strand it.
                e.currentTarget.setPointerCapture?.(e.pointerId);
            }
            this.lastX = e.clientX;
            this.lastY = e.clientY;
            this.userMoved = true;
            this.grabbed = true;
            // ONCE. `panBy` already carries dy into the camera; adding it again moved the canvas twice as
            // far vertically as the pointer, which reads as the graph sliding out from under the hand.
            this.apply(panBy({ zoom: this.zoom, panX: this.panX, panY: this.panY }, dx, dy));
        };
        this.up = (e) => {
            if (!this.dragging)
                return;
            this.dragging = false;
            const el = e.currentTarget;
            if (el.hasPointerCapture?.(e.pointerId))
                el.releasePointerCapture?.(e.pointerId);
            // Cleared on the NEXT frame so the click handler that fires immediately after pointerup can
            // still see that this was a drag.
            setTimeout(() => (this.moved = false), 0);
            this.requestUpdate();
        };
    }
    static { VitesseRunPipes_1 = this; }
    connectedCallback() {
        super.connectedCallback();
        this.tree?.addEventListener('change', this.onChange);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        // The subscription AND the poll — a tree left watching after its view is gone is a leak that
        // only ever shows up as traffic nobody can account for.
        this.tree?.removeEventListener('change', this.onChange);
        this.tree?.stop();
        this.ro?.disconnect();
        this.ro = null;
        if (this.settle)
            clearTimeout(this.settle);
        this.settle = null;
    }
    /**
     * THE STAGE'S SIZE IS WATCHED, not sampled whenever something else happens to re-render.
     *
     * `contW`/`contH` decide the viewport rectangle the minimap draws, and they were only re-read
     * inside `updated()` — which runs on a state change, not on a resize. So dragging the rail wider,
     * or resizing the window, left the blue rectangle describing a viewport that no longer existed
     * until the reader happened to pan or zoom. A map that is right only after you touch it is not a
     * map.
     */
    /**
     * …BUT NOT ON EVERY FRAME OF A DRAG.
     *
     * A re-render here is the whole graph — every node, every edge, the minimap — and `updated` then
     * measures the stage and re-frames the camera, which sets state and renders again. That is fine
     * for the thing this watcher was written for (a window resized, a column opened) and ruinous for
     * the thing it also catches: a reader dragging the wall beside this canvas. The observer fired
     * every frame, the main thread spent it rebuilding the graph, and the pointer events piled up —
     * so the divider crawled along well behind the cursor. Reported exactly that way, and worst when
     * the canvas was the column being resized.
     *
     * The graph does not depend on the width. Nothing about it needs to keep up with a drag: the
     * stage simply shows more or less of a picture that has not moved. So the re-render waits for the
     * resizing to STOP. In between, the canvas is static and the wall is free to track the pointer;
     * a fifth of a second after the last change it catches up in one pass.
     */
    static { this.SETTLE_MS = 180; }
    observe(el) {
        if (this.observed === el)
            return;
        this.ro?.disconnect();
        this.observed = el;
        this.ro = new ResizeObserver(() => {
            if (this.settle)
                clearTimeout(this.settle);
            this.settle = setTimeout(() => {
                this.settle = null;
                this.requestUpdate();
            }, VitesseRunPipes_1.SETTLE_MS);
        });
        this.ro.observe(el);
    }
    static { this.styles = [
        // The badge's own rules, adopted — this is a shadow root, so the global SCSS does not reach it.
        statusBadgeStyles,
        /*
         * AND THE ICON BUTTON'S.
         *
         * THE reason the HUD's zoom controls did not look like the flow canvas's: the markup was
         * identical, the per-instance sizing was identical, and the shared `.icon-btn` definition —
         * which is where the flat-transparent-with-a-blue-grow-on-hover treatment actually lives — was
         * never adopted into this shadow root. So the buttons were bare browser buttons with a width, a
         * height and a colour, sitting in a HUD that was correct around them.
         *
         * The flow canvas adopts exactly this. Adopting it rather than restating it is what stops the
         * two drifting again.
         */
        iconButton,
        css `
    :host {
      display: block;
      position: relative;
      height: 100%;
      min-height: 260px;
      overflow: hidden;
      /* NO GROUND OF ITS OWN — the same as the flows canvas, which is the point: two graph surfaces
         in one app that sit on different backgrounds read as two different products. The page owns
         the backdrop; this draws on it. */
    }
    .stage {
      position: absolute;
      inset: 0;
      cursor: grab;
      overflow: hidden;
      touch-action: none;
    }
    /* The HUD — one glass pill, minimap LEFT, zoom column RIGHT, both halves the same height. The
       flow canvas and the fleet map already draw this; matching it here is the point, so the same
       control reads the same wherever a graph is shown. */
    /*
     * A MESSAGE EDGE IS DASHED; a spawn edge is solid and still. The difference has to survive a
     * glance: one is structure, the other is traffic.
     *
     * TWO STATES, AND THEY ARE DIFFERENT FACTS. Blue and moving means a message is sitting unread —
     * these two agents are talking, right now, and the motion is the readout. Grey and still means
     * they talked and it landed: the relationship is worth drawing (it is why a tree of siblings is
     * not the whole picture of what a team did) and it is no longer news.
     *
     * The edge used to be blue always, because it existed only while it was pending — so an exchange
     * appeared for a poll or two and then vanished the moment it was read, and every conversation
     * that mattered was invisible by the time anyone looked at the canvas.
     */
    .note-edge {
      fill: none;
      stroke: var(--border-color, #2a2a30);
      stroke-width: 1.5;
      stroke-dasharray: 5 4;
      opacity: 0.5;
    }
    .note-edge.live {
      stroke: var(--blue);
      opacity: 0.85;
      animation: note-flow 0.9s linear infinite;
    }
    /* Quiet by default and blue only while it is live, so the arrowheads say the same thing the
       lines do — a canvas of grey arcs with blue tips would read as every exchange being in flight. */
    .note-arrow-head {
      fill: var(--border-color, #2a2a30);
    }
    .note-arrow-head-live {
      fill: var(--blue);
    }
    @keyframes note-flow {
      to {
        stroke-dashoffset: -9;
      }
    }
    /* Motion is information here, not decoration — it says a message is unread — but it is not worth
       overriding somebody's stated preference for stillness. The COLOUR still separates the two
       states, so nothing is lost by holding the dashes still. */
    @media (prefers-reduced-motion: reduce) {
      .note-edge.live {
        animation: none;
      }
    }
    .hud {
      position: absolute;
      right: 14px;
      bottom: 14px;
      z-index: 5;
      display: flex;
      flex-direction: row;
      align-items: stretch;
      gap: 8px;
      padding: 6px;
      border-radius: 14px;
      background: color-mix(in srgb, var(--bg-primary, #16181d) 80%, transparent);
      backdrop-filter: blur(12px);
      border: 1px solid var(--border-color);
      box-shadow: 0 6px 22px color-mix(in srgb, var(--shadow-color) 22%, transparent);
    }
    /* The box's SIZE is set inline — it tracks the stage's shape, which is not knowable here. */
    .mm {
      position: relative;
      border: 1px solid var(--border-color);
      border-radius: 9px;
      background: color-mix(in srgb, var(--bg-primary, #16181d) 86%, transparent);
      cursor: grab;
      overflow: hidden;
      transition: border-color 0.15s ease;
    }
    .mm:hover {
      border-color: var(--blue);
    }
    .mm:active {
      cursor: grabbing;
    }
    /* The scaled scene — the flow canvas's .mm-inner rule verbatim. */
    .mm-inner {
      position: absolute;
      top: 0;
      left: 0;
      transform-origin: 0 0;
      pointer-events: none;
    }
    /* The viewport frame — the fleet-map pattern: blue, rounded to the minimap's own corners. */
    .mm-view {
      position: absolute;
      border: 1.5px solid var(--blue);
      border-radius: 9px;
      background: rgb(from var(--blue) r g b / 0.1);
      box-shadow: 0 0 6px rgb(from var(--blue) r g b / 0.55);
      pointer-events: none;
    }
    /* The vertical rule between the map and the buttons — present on the flows HUD and missing here,
       which is most of why the two did not read as the same control. */
    .hud-sep {
      width: 1px;
      align-self: stretch;
      background: var(--border-color);
    }
    .zoombar .icon-btn {
      width: 30px;
      height: 30px;
      color: var(--text-secondary);
    }
    .zoombar {
      display: flex;
      justify-content: space-between; /* span the minimap's full height — no shift */
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      gap: 2px;
    }
    .zoombar .icon-btn {
      width: 30px;
      height: 30px;
      color: var(--text-secondary);
      font-size: 15px;
      line-height: 1;
    }
    /* THE CONVERSATION'S CARD. The same card, tinted BLUE — the product's own accent, and the
       colour every other "this is where you are" affordance in the app already uses. The AI pink was
       borrowed from the assistant's branding and read as a warning next to the run states beside
       it, which is the one thing a starting point should not do. */
    .card.origin {
      border-color: color-mix(in srgb, var(--blue) 45%, var(--border-color));
      background: color-mix(in srgb, var(--blue) 7%, var(--bg-primary));
    }
    .card.origin .name {
      color: var(--text-primary);
    }
    .stage.dragging {
      cursor: grabbing;
    }
    .layer {
      position: absolute;
      left: 0;
      top: 0;
      transform-origin: 0 0;
    }
    svg {
      position: absolute;
      left: 0;
      top: 0;
      overflow: visible;
      pointer-events: none;
    }
    /*
     * THE FLOW CANVAS'S WIRES, transcribed.
     *
     * Every edge is a quiet base line, and an edge with work moving down it gets a dashed, glowing
     * overlay on top — two paths, not one coloured line. This file used to paint the whole wire in
     * the run's own colour: solid green for done, solid red for stopped, so a finished tree was a
     * web of hard green and a canvas with one failure was a web of hard red. The colour said
     * nothing the card's badge did not already say, and it said it in the loudest possible place.
     *
     * The base line carries structure; the flow overlay carries motion. Nothing else is coloured.
     */
    .wire-base {
      fill: none;
      stroke: var(--border-color, #2a2a30);
      stroke-width: 1.5;
      opacity: 0.55;
    }
    .wire-flow {
      fill: none;
      stroke: var(--blue, #1a8cd8);
      stroke-width: 2;
      stroke-linecap: round;
      stroke-dasharray: 5 11;
      filter: drop-shadow(0 0 3px rgb(from var(--blue, #1a8cd8) r g b / 0.7));
      animation: wire-flow 0.9s linear infinite;
    }
    @keyframes wire-flow {
      to {
        stroke-dashoffset: -16;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .wire-flow {
        animation: none;
      }
    }
    .card {
      position: absolute;
      width: ${CARD_W}px;
      height: ${CARD_H}px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 5px;
      padding: 8px 10px;
      border: 1px solid var(--border-color);
      border-radius: 10px;
      background: var(--bg-primary, #fff);
      font: inherit;
      text-align: left;
      color: var(--text-primary);
      cursor: pointer;
      overflow: hidden;
    }
    .card:hover {
      border-color: var(--blue);
    }
    .card:disabled {
      cursor: default;
    }
    .card.running {
      border-color: color-mix(in srgb, var(--blue) 45%, var(--border-color));
    }
    .card.stopped {
      border-color: color-mix(in srgb, var(--red) 45%, var(--border-color));
    }
    /* A superseded run is still on the canvas — deleting it would hide that the work happened —
       but it must not read as current. */
    .card.stale {
      opacity: 0.55;
    }
    .head {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }
    .mark {
      display: inline-flex;
      flex-shrink: 0;
      color: var(--text-secondary);
    }
    .running .mark {
      color: var(--blue);
      animation: spin 1.4s linear infinite;
    }
    .done .mark {
      color: var(--green);
    }
    .stopped .mark {
      color: var(--red);
    }
    .awaiting .mark {
      color: var(--yellow, var(--text-secondary));
    }
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .mark {
        animation: none !important;
      }
    }
    .name {
      font-weight: 600;
      font-size: 12px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
      flex: 1;
    }
    /* The product's badge, at the size a 210px card can hold. Nothing else is restyled: the colour,
       the radius and the weight are the component's, so a card reads like a row in a list. */
    .card .base-status-badge {
      flex-shrink: 0;
      font-size: 9.5px;
      padding: 2px 6px;
    }
    /* TASK and DELIV are the two lines that make a node legible without opening it: what it was
       asked for, and what it owes back. They are the reason this is a card and not a row. */
    .field {
      display: flex;
      gap: 6px;
      align-items: baseline;
      min-width: 0;
      font-size: 11px;
    }
    .key {
      flex-shrink: 0;
      font-size: 8.5px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--text-secondary);
      opacity: 0.75;
      width: 34px;
    }
    .val {
      color: var(--text-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
    .foot {
      margin-top: auto;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 9.5px;
      color: var(--text-secondary);
      font-variant-numeric: tabular-nums;
    }
    .foot .right {
      margin-left: auto;
      white-space: nowrap;
    }
    .hint {
      position: absolute;
      right: 10px;
      bottom: 8px;
      font-size: 10px;
      color: var(--text-secondary);
      opacity: 0.7;
      pointer-events: none;
      user-select: none;
    }
    .err,
    .none {
      padding: 14px;
      font-size: 12px;
      color: var(--text-secondary);
    }
  `,
    ]; }
    render() {
        const tree = this.tree;
        if (!tree)
            return nothing;
        if (tree.error)
            return html `<div class="err">${tree.error}</div>`;
        if (!tree.roots.length)
            return html `<div class="none">${t('ai.pipesEmpty')}</div>`;
        const placed = this.place();
        const origin = this.originCard();
        const w = this.stageW(placed);
        const h = this.stageH(placed);
        const at = new Map(placed.map((p) => [p.node.run.runId, p]));
        return html `
      <div
        class="stage ${this.dragging ? 'dragging' : ''}"
        @wheel=${this.onWheel}
        @pointerdown=${this.down}
        @pointermove=${this.move}
        @pointerup=${this.up}
        @pointercancel=${this.up}
        @pointerleave=${this.up}
      >
        <div
          class="layer"
          style="transform:translate(${this.panX}px,${this.panY}px) scale(${this.zoom}); transform-origin: 0 0"
        >
          ${this.scene(placed, at, w, h, origin)}
        </div>
        ${this.hud({ contW: this.contW, contH: this.contH, w, h }, placed, at, origin)}
      </div>
    `;
    }
    /** Every run, placed — one column reserved on the left when there is an origin card to put in it. */
    place() {
        return layout(this.tree?.roots ?? [], this.origin ? 1 : 0);
    }
    /** Where the origin card goes, or nothing when the host did not name a conversation. */
    originCard() {
        return this.origin ? originAt(this.tree?.roots.length ?? 0) : null;
    }
    /**
     * THE GRAPH, drawn identically wherever it appears.
     *
     * Extracted so the minimap can render THE SAME wires and THE SAME cards, just scaled — which is
     * what the flow canvas does and what makes the two read as one control. Drawing coloured blocks
     * instead was a second, simpler picture of the graph that agreed with the real one only by
     * coincidence: nothing kept a block's shape, its edges or its mesh in step with the card it stood
     * for, and the map stopped resembling the thing it was a map of the moment either changed.
     */
    scene(placed, at, w, h, origin) {
        return html `<svg width=${w} height=${h}>
        <defs>
          <!-- Direction is the point of a mesh edge: which agent messaged which. An undirected
               line between two peers says they are related and not which way it went. -->
          <!-- Two heads because a marker cannot inherit the stroke of the path that uses it: an
               arrowhead is filled, and a fill declared on the marker resolves against the marker
               rather than the referrer. One per state keeps the tip the colour of its own arc. -->
          <marker id="note-arrow-live" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L8,4 L0,8 z" class="note-arrow-head-live" />
          </marker>
          <marker id="note-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L8,4 L0,8 z" class="note-arrow-head" />
          </marker>
        </defs>${placed.flatMap((p) => this.edges(p, at))}${origin ? this.originEdges(origin) : nothing}${this.mesh(at, origin)}</svg>
      ${origin ? this.renderOrigin(origin) : nothing}${placed.map((p) => this.card(p))}`;
    }
    /**
     * The corner HUD — a minimap and the zoom controls, the same pairing the flow canvas and the
     * fleet map use. A tree of twenty agents does not fit a side rail at a readable size, so the
     * choice is between a graph you cannot read and one you cannot navigate; the minimap is what makes
     * the third option (zoom out, then go somewhere) actually usable, because it says where "here" is
     * while you are zoomed in.
     */
    hud(e, placed, at, origin) {
        const vr = viewportRect({ zoom: this.zoom, panX: this.panX, panY: this.panY }, e);
        // MEET-FIT AND CENTRED — the flows HUD's `miniFit`, transcribed. It was a per-axis stretch here
        // (sx for x, sy for y), so the scene inside was drawn at one uniform scale while the viewport
        // rectangle over it was computed at two: the blue frame did not sit on the part of the graph it
        // claimed to, which is the only thing a minimap has to get right. One scale, one pair of
        // offsets, used by the drawing, by the frame and by the scrub, so all three agree.
        const { s, ox, oy } = this.miniFit(e);
        const box = this.mmBox();
        return html `
      <div class="hud" @pointerdown=${(ev) => ev.stopPropagation()}>
        <div
          class="mm"
          style=${`width:${box.w}px;height:${box.h}px`}
          @pointerdown=${(ev) => this.scrub(ev, e)}
          @pointermove=${(ev) => this.scrub(ev, e)}
        >
          <!-- The scaled-down scene: the EXACT same wires and cards as the stage, never a
               simplified stand-in for them. Pointer events are off inside it so the map itself
               owns the click and a card cannot swallow a scrub. -->
          <div
            class="mm-inner"
            aria-hidden="true"
            style=${`width:${e.w}px;height:${e.h}px;transform:translate(${ox.toFixed(1)}px,${oy.toFixed(1)}px) scale(${s.toFixed(4)})`}
          >
            ${this.scene(placed, at, e.w, e.h, origin)}
          </div>
          <span
            class="mm-view"
            style=${`left:${(ox + vr.x * s).toFixed(1)}px;top:${(oy + vr.y * s).toFixed(1)}px;width:${(vr.w * s).toFixed(1)}px;height:${(vr.h * s).toFixed(1)}px`}
          ></span>
        </div>
        <div class="hud-sep"></div>
        <div class="zoombar">
          <button class="icon-btn" title=${t('ai.pipesZoomIn')} aria-label=${t('ai.pipesZoomIn')} @click=${() => this.step(1.35)}>
            <vitesse-icon name="plus" size="16"></vitesse-icon>
          </button>
          <button class="icon-btn" title=${t('ai.pipesZoomOut')} aria-label=${t('ai.pipesZoomOut')} @click=${() => this.step(1 / 1.35)}>
            <vitesse-icon name="minus" size="16"></vitesse-icon>
          </button>
          <button class="icon-btn" title=${t('ai.pipesFit')} aria-label=${t('ai.pipesFit')} @click=${() => this.doFit(e)}>
            <vitesse-icon name="maximize" size="16"></vitesse-icon>
          </button>
        </div>
      </div>
    `;
    }
    /*
     * MEASURE THE STAGE, and frame the graph until somebody chooses a view.
     *
     * A tree streams in: one node, then three, then twelve. Framing on every change would be the
     * canvas rearranging itself while you read it, and framing never would leave a growing graph
     * walking off the edge. So it auto-frames only while `userMoved` is false — the moment a pan,
     * zoom or scrub happens, the camera is the reader's and is left alone.
     */
    updated() {
        const el = this.renderRoot.querySelector('.stage');
        if (!el)
            return;
        this.observe(el);
        const { width, height } = el.getBoundingClientRect();
        if (!width || !height)
            return;
        const changed = width !== this.contW || height !== this.contH;
        this.contW = width;
        this.contH = height;
        const journey = this.focusRunId || (this.hasFrom ? `out:${this.fromRunId}` : '');
        if (journey && journey !== this.flownTo)
            this.fly(journey);
        if (!this.userMoved) {
            const layer = this.renderRoot.querySelector('.layer svg');
            const w = Number(layer?.getAttribute('width') ?? 0);
            const h = Number(layer?.getAttribute('height') ?? 0);
            const c = fit({ contW: width, contH: height, w, h }, 1);
            // Compared before assigning: apply() sets reactive state, and setting it to what it already
            // holds inside updated() is how a render loop starts.
            if (c && (changed || c.zoom !== this.zoom || c.panX !== this.panX || c.panY !== this.panY))
                this.apply(c);
        }
    }
    /**
     * Travel to one run: settle on where it came FROM, then move across, then close in.
     *
     * Three legs rather than one because that is the sentence the movement is making — "here is the
     * agent you were reading, here is the one it started, now here it is up close". Collapsed into a
     * single tween the pan and the zoom fight each other: the card rushes at the reader while it is
     * still crossing the canvas, and the relationship between the two agents — the thing the journey
     * exists to show — is the part that gets lost.
     *
     * Honours prefers-reduced-motion by arriving immediately. Someone who has asked for less motion
     * has asked for exactly this kind of less.
     */
    /** The card a node id names — '' is the conversation card, which is not a run. */
    cardOf(id, placed) {
        if (!id)
            return this.originCard();
        return placed.find((p) => p.node.run.runId === id) ?? null;
    }
    /**
     * ONE JOURNEY, PLAYED FROM WHEREVER THE READER CAME FROM.
     *
     * Two entries to this surface, one movement:
     *
     *   · opening the canvas FROM a thread — start close on that thread's own card and pull back to
     *     the forest. The half that answers "where am I".
     *   · crossing to another agent — the same pull-back, then across, then in. The half that answers
     *     "where am I" followed by the half that answers "and where is that one".
     *
     * They were two code paths and the first did not exist, so entering the canvas was a cut to a
     * framed graph with nothing saying which card you had just been reading.
     */
    fly(journey) {
        const tree = this.tree;
        if (!tree || !this.contW || !this.contH)
            return;
        const placed = this.place();
        const runId = journey.startsWith('out:') ? '' : journey;
        const from = this.hasFrom ? this.cardOf(this.fromRunId, placed) : null;
        const target = runId ? placed.find((p) => p.node.run.runId === runId) : null;
        // Not in this forest yet — a later poll will bring it, and updated() retries.
        if (runId && !target)
            return;
        if (!runId && !from)
            return;
        this.flownTo = journey;
        this.userMoved = true; // a chosen framing; auto-fit must not yank it back
        const e = { contW: this.contW, contH: this.contH, w: this.stageW(placed), h: this.stageH(placed) };
        const centreOfCard = (p) => [p.x + CARD_W / 2, p.y + CARD_H / 2];
        const legs = [];
        if (from) {
            const [fx, fy] = centreOfCard(from);
            // START INSIDE the card the reader just left — not animated TO, because that is where they
            // already are; it is the frame the movement departs from. At `enterZoom`, which is the zoom
            // the arrival ENDED on, so leaving picks up exactly where entering put them.
            this.apply(framedOn(e, fx, fy, enterZoom(this.contW, this.contH)));
            if (!target) {
                /*
                 * OUT — and that is the whole movement.
                 *
                 * The thread shrinks away from the reader and the card it belonged to is revealed
                 * underneath it, already centred, with two card-widths of room on every side. Nothing is
                 * crossed: the card you left is the subject of this movement, and travelling on to its
                 * parent afterwards answered a question nobody asked while moving the reader off the one
                 * thing they had just been looking at.
                 *
                 * It is precisely the arrival below, played backwards — same two framings, opposite order,
                 * one symmetric ease across it — which is what makes leaving and entering read as one
                 * relationship rather than as two animations that happen to involve the same card.
                 */
                legs.push({ to: framedOn(e, fx, fy, revealZoom(this.contW, this.contH)), ms: FLIGHT_MS });
            }
        }
        if (target) {
            const [tx, ty] = centreOfCard(target);
            /*
             * WHERE THE JOURNEY BEGINS when nobody said. A nested agent starts at its parent, which is
             * the card it came out of. A ROOT agent's parent is a synthetic turn id with no card behind
             * it, so the journey used to have no first leg at all and the reader was dropped on the
             * destination. Its parent is the conversation, and the conversation has a card.
             */
            const rz = revealZoom(this.contW, this.contH);
            /*
             * IN — the exact mirror, plus the recentre when the journey starts inside another card.
             *
             * From a canvas the reader is already looking at, this is ONE move: the camera recentres on
             * the card and closes on it at the same time, every axis together, the way a camera actually
             * moves. Two separate movements — pan, then zoom — is what made this read as machinery.
             *
             * From INSIDE another thread (card to card) the same move gains a middle: out to the reveal
             * framing on the card being left, across to the one being opened, and in. The crossing does
             * not slow down and does not change zoom — it inherits the pulled-back framing the first part
             * built, so the whole thing is one continuous arc that happens to pass over the tree.
             */
            if (from) {
                const [px, py] = centreOfCard(from);
                legs.push({ to: framedOn(e, px, py, rz), ms: FLIGHT_MS }); // out, off the card being left
                legs.push({ to: framedOn(e, tx, ty, rz), ms: CROSS_MS }); // across, at that same framing
            }
            legs.push({ to: framedOn(e, tx, ty, enterZoom(this.contW, this.contH)), ms: FLIGHT_MS }); // and in
        }
        if (!legs.length)
            return;
        this.grabbed = false; // a fresh journey; whatever happened before it is not a refusal of this one
        const landed = () => this.onArrive?.(runId, target?.node.run.conversationId ?? '');
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.apply(legs[legs.length - 1].to);
            landed();
            return;
        }
        void this.travel(legs).then(() => {
            /*
             * ARRIVAL IS NOT CONDITIONAL ON THE ANIMATION.
             *
             * It used to be gated on the travel resolving true — every leg run to its last frame. That
             * made stepping into the agent depend on a rAF chain nothing guarantees — a re-render, a
             * superseding flight, a backgrounded tab — and when it did not fire the reader was left parked
             * on the canvas looking at the agent they had asked to open, with no way to tell that anything
             * had gone wrong. The journey is decoration; going where you clicked is the feature, and the
             * feature must not be the thing that drops.
             *
             * The ONE refusal that still counts is a hand on the canvas: someone who panned or zoomed
             * mid-flight has chosen this surface, and taking it away from them would be the rudest
             * possible reading of a click they made a second earlier.
             */
            if (!this.grabbed)
                landed();
        });
    }
    /**
     * Walk the camera through a list of framings — as ONE movement, not as a series of them.
     *
     * IT USED TO BE A LOOP OF SEPARATE ANIMATIONS, and that is what made it feel like machinery
     * rather than travel. Each leg ran its own rAF chain with its own ease from t=0 to t=1, and an
     * ease that starts and ends at rest does exactly that at every seam: the camera decelerated to a
     * complete stop at each waypoint and accelerated again from standing. A three-leg journey — pull
     * back to the parent, cross to the child, dive in — was three separate movements with two dead
     * stops in them, plus a dropped frame at each seam where one promise resolved and the next rAF
     * was scheduled. Described exactly as it looked: cubic and blocky, with a pause between each
     * motion, instead of one continuous thing.
     *
     * ONE CLOCK, ONE EASE, over the whole path. The legs become WAYPOINTS on a single timeline: the
     * ease is applied once across the total duration, and the eased progress is then mapped onto the
     * concatenated segments and interpolated LINEARLY within whichever segment it falls in. So the
     * acceleration profile belongs to the journey — build speed, cruise through the middle waypoints
     * without stopping, settle at the destination — and the waypoints only steer.
     *
     * Per-leg `ms` still sets how much of the timeline a leg is worth, so the dive stays the short
     * accelerating last part of the movement rather than a third of it. Per-leg `ease` no longer
     * applies and cannot: an ease per segment is precisely the thing that produced the stops.
     *
     * Resolves false if a newer journey — or the reader's own hand — took over before it landed.
     */
    async travel(legs) {
        const token = ++this.flying;
        // The path, starting where the camera actually is. Durations are the weights along it.
        const start = { zoom: this.zoom, panX: this.panX, panY: this.panY };
        const points = [start, ...legs.map((l) => l.to)];
        const spans = legs.map((l) => Math.max(1, l.ms ?? LEG_MS));
        const total = spans.reduce((a, b) => a + b, 0);
        // Where each waypoint sits on the 0..1 timeline, so a short dive occupies a short stretch of it.
        const marks = [0];
        let run = 0;
        for (const ms of spans) {
            run += ms;
            marks.push(run / total);
        }
        const began = performance.now();
        return new Promise((done) => {
            const frame = () => {
                // A newer journey (or a hand on the canvas) supersedes this one — the reader wins.
                if (token !== this.flying)
                    return done(false);
                const t = Math.min(1, (performance.now() - began) / total);
                /*
                 * ONE EASE, over the whole journey: slow, fast, slow.
                 *
                 * Symmetric, so reversing the waypoints reverses the motion exactly — which is what makes
                 * leaving a thread the arrival played backwards without anything here mirroring a curve by
                 * hand. It overshot briefly (`easeInOutBack`) on the theory that a little bounce reads as
                 * physical; on a camera it reads as a wobble at both ends of every move, so it is gone.
                 */
                const k = easeInOutCubic(t);
                // Which segment the eased progress lands in, and how far through it.
                let i = 1;
                while (i < marks.length - 1 && k > marks[i])
                    i++;
                const span = marks[i] - marks[i - 1] || 1;
                const local = Math.min(1, Math.max(0, (k - marks[i - 1]) / span));
                // LINEAR within the segment: the shape of the movement is the global ease, and easing again
                // here would re-introduce a stop at every waypoint, which is the whole bug.
                this.apply(lerpCamera(points[i - 1], points[i], local, (x) => x));
                if (t >= 1)
                    return done(true);
                requestAnimationFrame(frame);
            };
            requestAnimationFrame(frame);
        });
    }
    /* The extent has to cover the origin card too: it sits in a column the runs do not occupy, and a
       stage measured from the runs alone clips it out of the fit and out of the minimap. */
    stageW(placed) {
        const o = this.originCard();
        return Math.max(o ? o.x + CARD_W : 0, ...placed.map((p) => p.x + CARD_W)) + PAD;
    }
    stageH(placed) {
        const o = this.originCard();
        return Math.max(o ? o.y + CARD_H : 0, ...placed.map((p) => p.y + CARD_H)) + PAD;
    }
    apply(c) {
        this.zoom = c.zoom;
        this.panX = c.panX;
        this.panY = c.panY;
    }
    step(factor) {
        this.userMoved = true;
        this.apply(zoomBy({ zoom: this.zoom, panX: this.panX, panY: this.panY }, { contW: this.contW, contH: this.contH, w: 0, h: 0 }, factor));
    }
    doFit(e) {
        const c = fit(e, 1);
        if (!c)
            return;
        // A deliberate fit is the one camera move that RESETS the auto-frame: the reader has asked to
        // see the whole thing, which is exactly what auto-framing would have given them.
        this.userMoved = false;
        this.apply(c);
    }
    /** The box. Fixed — see MM_W. A method rather than two constants at the call sites because the
     *  fit, the frame and the hit-test all have to be asking the same thing. */
    mmBox() {
        return { w: MM_W, h: MM_H };
    }
    /** The meet-fit scale and centring offsets mapping graph coordinates into the fixed minimap box.
     *  ONE function, used for drawing, for the viewport frame and for hit-testing, which is the only
     *  arrangement in which the three cannot disagree. The 0.92 is the flows HUD's, so the graph has
     *  the same breathing room inside the box on both surfaces. */
    miniFit(e) {
        const { w: mw, h: mh } = this.mmBox();
        const s = e.w > 0 && e.h > 0 ? Math.min(mw / e.w, mh / e.h) * 0.92 : 1;
        return { s, ox: (mw - e.w * s) / 2, oy: (mh - e.h * s) / 2 };
    }
    /** Drag anywhere on the minimap to bring that part of the graph to the middle. */
    scrub(ev, e) {
        if (ev.type === 'pointermove' && ev.buttons === 0)
            return;
        const r = ev.currentTarget.getBoundingClientRect();
        const { s, ox, oy } = this.miniFit(e);
        const box = this.mmBox();
        this.userMoved = true;
        this.grabbed = true;
        this.apply(centreOn({ zoom: this.zoom, panX: this.panX, panY: this.panY }, e, (((ev.clientX - r.left) / r.width) * box.w - ox) / s, (((ev.clientY - r.top) / r.height) * box.h - oy) / s));
    }
    /**
     * PEER MESSAGES — the edges that make this a mesh rather than a tree.
     *
     * A spawn edge runs down the hierarchy and always in one direction. A message runs between any two
     * agents, in either direction, and says something the tree structure cannot: that these two are
     * talking. Drawn differently on purpose — dashed, and routed as a flat arc rather than the spawn
     * edge's vertical S — because a reader who cannot tell the two apart at a glance is reading a
     * picture that claims a hierarchy which does not exist.
     *
     * BOTH STATES ARE DRAWN. These used to be in-flight messages only: an edge existed while the
     * message was unread and went the moment it was consumed, so an exchange showed for a poll or two
     * and then vanished, and a team that had spent a run correcting each other looked like siblings
     * that never met. The blue-and-moving edge still means "unread"; a read one goes quiet and stays,
     * because the relationship is the thing worth seeing after the traffic has stopped.
     */
    mesh(at, origin) {
        const notes = this.tree?.notes ?? [];
        return notes.map((n) => {
            /*
             * A MESSAGE FROM THE CONVERSATION ITSELF STARTS AT THE CONVERSATION'S CARD.
             *
             * The orchestrating turn is not a run and has no card of its own, so anchoring only on runs
             * silently dropped the ordinary case — a person's turn discovers the team, broadcasts to both
             * agents and messages one by name, and none of the three drew anything. The origin card IS
             * that conversation on this canvas, which is where those arrows belong.
             */
            const a = at.get(n.from) ?? (origin && !at.has(n.from) ? { x: origin.x, y: origin.y } : undefined);
            const b = at.get(n.to);
            // A message whose sender or receiver is not on this canvas — a peer in another tree — is not
            // drawn rather than drawn to an edge of the stage, which would point at nothing.
            if (!a || !b)
                return nothing;
            const x1 = a.x + CARD_W / 2;
            const y1 = a.y + CARD_H / 2;
            const x2 = b.x + CARD_W / 2;
            const y2 = b.y + CARD_H / 2;
            // Bowed perpendicular to the run, so a pair messaging BOTH ways draws two arcs rather than
            // one line over itself, and neither hides under the cards it connects.
            const dx = x2 - x1;
            const dy = y2 - y1;
            const len = Math.hypot(dx, dy) || 1;
            const bow = Math.min(70, len * 0.22);
            const cx = (x1 + x2) / 2 - (dy / len) * bow;
            const cy = (y1 + y2) / 2 + (dx / len) * bow;
            /*
             * LIVE IS UNREAD, NOT UNDELIVERED — a message is durable the moment it is sent. The blue says
             * the recipient has not reached the step boundary that picks it up, which is the thing a
             * person watching this is actually asking.
             *
             * AND A FINISHED RUN WILL NEVER REACH ANOTHER ONE. `pending` is read off the recipient's
             * inbox, which does not empty when a run ends, so a note that arrived just too late stayed
             * blue and animated for the life of the canvas — two Completed cards joined by a pulsing
             * dependency that could not possibly still be in flight. The claim the colour makes is about
             * the FUTURE, so it has to be false once there is no future to reach it in.
             */
            const recipient = this.tree?.run(n.to);
            const live = n.pending && (recipient ? runState(recipient) !== 'done' && runState(recipient) !== 'stopped' : true);
            return svg `<path
        class="note-edge ${live ? 'live' : ''}"
        d=${`M${x1},${y1} Q${cx},${cy} ${x2},${y2}`}
        marker-end=${live ? 'url(#note-arrow-live)' : 'url(#note-arrow)'}
      />`;
        });
    }
    /** One edge per child, curved so parallel siblings stay distinguishable where they converge.
     *  Out of the parent's RIGHT edge and into the child's LEFT one, because the tree reads across. */
    edges(p, at) {
        const x1 = p.x + CARD_W;
        const y1 = p.y + CARD_H / 2;
        return p.node.children.map((c) => {
            const q = at.get(c.run.runId);
            if (!q)
                return nothing;
            // The edge takes the CHILD's state: it is the pipe the child's work flows through.
            return this.wire(x1, y1, q.x, q.y + CARD_H / 2, runState(c.run));
        });
    }
    /** One horizontal S between two points — a quiet base line, plus the dashed flow overlay when
     *  work is moving down it. Shared by the spawn edges and by the origin's, so the conversation's
     *  line into its first agents is the same line every other spawn draws. */
    wire(x1, y1, x2, y2, state) {
        const mid = (x1 + x2) / 2;
        const d = `M${x1},${y1} C${mid},${y1} ${mid},${y2} ${x2},${y2}`;
        return svg `<path class="wire-base" d=${d} />${state === 'running' ? svg `<path class="wire-flow" d=${d} />` : nothing}`;
    }
    /** The wires out of the conversation into the agents its turns started. Drawn with the ROOT's own
     *  state, exactly as every other edge takes its child's — the line says how the work below it is
     *  going, and that is as true of the first generation as of any other. */
    originEdges(o) {
        const x1 = o.x + CARD_W;
        const y1 = o.y + CARD_H / 2;
        const placed = this.place();
        return (this.tree?.roots ?? []).map((r) => {
            const q = placed.find((p) => p.node.run.runId === r.run.runId);
            if (!q)
                return nothing;
            return this.wire(x1, y1, q.x, q.y + CARD_H / 2, runState(r.run));
        });
    }
    /**
     * THE CONVERSATION, as a card.
     *
     * A distinct treatment on purpose: it has no status, no cost and no steps, so a card wearing the
     * run chrome with all of it blank would read as an agent that had failed to start. What it has is
     * a name and a way in, and it says only those.
     */
    renderOrigin(o) {
        const origin = this.origin;
        if (!origin)
            return html ``;
        return html `
      <button
        class="card origin"
        style="left:${o.x}px;top:${o.y}px"
        ?disabled=${!this.onOpenRun}
        title=${origin.title}
        @click=${() => {
            if (this.moved)
                return;
            // No run id: this is a conversation, and the host opens it as one. A fabricated id here
            // would send the reader to a run that does not exist.
            this.onOpenRun?.('', origin.conversationId);
        }}
      >
        <!-- NO ICON. Every other card wears a mark because its mark carries the run's STATE, which
             is a thing worth a glance. A conversation has no state, so the icon here was decoration
             standing in the position where the rest of the canvas puts information. -->
        <div class="head">
          <span class="name">${origin.title}</span>
        </div>
        <!-- THE SAME TWO LINES ITS AGENTS HAVE. What was asked, and what it owes back — the pair
             that makes a node legible without opening it. A conversation's task is the person's own
             opening message; what it owes back is the answer in the thread, which is where the
             reader is standing when they click it. -->
        ${origin.task
            ? html `<div class="field"><span class="key">${t('ai.pipesTask')}</span><span class="val">${origin.task}</span></div>`
            : nothing}
        <div class="field">
          <span class="key">${t('ai.pipesDeliv')}</span><span class="val">${t('ai.pipesOriginHelp')}</span>
        </div>
        <div class="foot">${statusBadge('blue', t('ai.messageCount', { count: origin.messages }))}</div>
      </button>
    `;
    }
    card(p) {
        /*
         * THE NEWEST ATTEMPT is what the card reports — a woken or resumed agent is the same agent, and
         * its state is where it is NOW, not where its first attempt stopped. The task and deliverable are
         * the first attempt's, because that is what it was asked for; the attempts are counted in the
         * foot so a card that has been woken says so.
         */
        const run = latestAttempt(p.node);
        const first = p.node.run;
        const role = this.experts?.find((e) => e.id === first.expertId);
        // Its whole life and everything it spent, across every attempt — not the last wake alone.
        const span = { startedAtNs: first.startedAtNs, endedAtNs: run.endedAtNs };
        const spent = sumUsage([first, ...p.node.attempts]);
        const state = runState(run);
        const mark = state === 'running' ? 'orbit' : state === 'done' ? 'check' : state === 'stopped' ? 'x' : 'zap';
        const enterable = Boolean(this.onOpenRun && run.conversationId);
        const tok = runTokens(spent);
        return html `
      <button
        class="card ${state} ${run.stale ? 'stale' : ''}"
        style="left:${p.x}px;top:${p.y}px"
        ?disabled=${!enterable}
        title=${run.headline || run.task?.objective || ''}
        @click=${() => {
            // A drag that panned the canvas is not a selection. Without this, letting go of a pan
            // over a card steps into it, which makes the canvas feel like it is grabbing at you.
            if (this.moved)
                return;
            this.onOpenRun?.(run.runId, run.conversationId);
        }}
      >
        <!-- THE APP'S OWN BADGE, and one thing saying one thing. There was a tick beside the name
             AND the word "Completed" beside that, in a rounded pill this file drew itself — two
             spellings of the same fact and a third spelling of a component the product already
             has. The badge says it; the tick said it again. -->
        <div class="head">
          <span class="name" title=${run.expertId}>${runLabel(run)}</span>
        </div>
        ${run.task?.objective
            ? html `<div class="field"><span class="key">${t('ai.pipesTask')}</span><span class="val">${run.task.objective}</span></div>`
            : nothing}
        ${run.task?.deliverableSpec
            ? html `<div class="field">
                <span class="key">${t('ai.pipesDeliv')}</span><span class="val">${run.task.deliverableSpec}</span>
              </div>`
            : nothing}
        ${first.expertId
            ? html `<div class="field">
                <span class="key">${t('ai.pipesRole')}</span><span class="val">${role?.displayName || first.expertId}</span>
              </div>`
            : nothing}
        <!--
          TWO LINES AND A FOOT, the same as the conversation card it hangs off.

          Four more used to follow — SOURCE NOT RETURNED, WRONG SUBJECT, NOT MEASURED, SOURCES — in
          red and amber, each one a comma-joined list ellipsised after four words in a 34px key
          column. So every finished agent wore a block of coloured fragments that could not be read
          and could not be acted on, and the canvas looked like a wall of errors whether or not
          anything was wrong.

          None of it is lost: the findings are keys in the return's payload, one row above in the
          thread, where a reader who wants to check provenance is already standing and where there
          is room to actually show it. A card is for reading the SHAPE of a tree at a glance, and a
          warning nobody can read is not a warning.
        -->
        <!-- WHAT IT SPENT, in the badges the rest of the product measures things in. The model
             name is gone: it is the longest string on the card, it is the same on every card in a
             tree, and nobody reading the SHAPE of a fan-out is choosing a provider. It is still one
             click away, in the agent's own header. -->
        <!-- ALL THE BADGES IN ONE PLACE, and the state first. It sat alone up beside the name,
             which put one measurement of this run in the title row and the rest at the foot — two
             badge rows on a 210px card, for no reason except that the state used to be a pill. -->
        <div class="foot">
          ${statusBadge(state === 'done' ? 'green' : state === 'stopped' ? 'red' : state === 'running' ? 'blue' : 'yellow', runStateLabel(state))}
          ${run.stale ? statusBadge('yellow', t('ai.runStale')) : nothing}
          ${statusBadge('blue', runElapsed(span))}
          ${p.node.attempts.length ? statusBadge('yellow', t('ai.pipesWoken', { n: p.node.attempts.length })) : nothing}
          ${tok ? statusBadge('blue', t('ai.tokenCount', { n: tok })) : nothing}
        </div>
      </button>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseRunPipes.prototype, "tree", void 0);
__decorate([
    property({ attribute: false })
], VitesseRunPipes.prototype, "onOpenRun", void 0);
__decorate([
    property({ attribute: false })
], VitesseRunPipes.prototype, "focusRunId", void 0);
__decorate([
    property({ attribute: false })
], VitesseRunPipes.prototype, "fromRunId", void 0);
__decorate([
    property({ attribute: false })
], VitesseRunPipes.prototype, "hasFrom", void 0);
__decorate([
    property({ attribute: false })
], VitesseRunPipes.prototype, "origin", void 0);
__decorate([
    property({ attribute: false })
], VitesseRunPipes.prototype, "experts", void 0);
__decorate([
    property({ attribute: false })
], VitesseRunPipes.prototype, "onArrive", void 0);
__decorate([
    state()
], VitesseRunPipes.prototype, "zoom", void 0);
__decorate([
    state()
], VitesseRunPipes.prototype, "panX", void 0);
__decorate([
    state()
], VitesseRunPipes.prototype, "panY", void 0);
VitesseRunPipes = VitesseRunPipes_1 = __decorate([
    customElement('vitesse-run-pipes')
], VitesseRunPipes);
export { VitesseRunPipes };
/**
 * Place every node: depth ACROSS, spawn order down.
 *
 * LEFT TO RIGHT, because that is the sentence the picture makes: here is the conversation, here is
 * what it started, here is what those started. It ran top-to-bottom before, which put the first
 * generation of agents in a wide row that a rail-width canvas could not hold — the reader saw two
 * cards and a lot of empty space below them, and had to pan sideways to learn a tree had four
 * children. Depth is the axis that grows slowly (three or four levels at most); spawn count is the
 * one that grows without limit, so spawn count gets the axis with a scrollbar on it.
 *
 * Breadth-first, so siblings sit together and a parent is always left of its children. Rows are
 * assigned per DEPTH rather than per parent, which keeps the canvas short — a per-parent row
 * allocation grows the height with every leaf.
 *
 * `colOffset` reserves the first column for the conversation that started the forest, which is a
 * card the tree does not own: it is not a run, it has no record, and the view draws it. Passing the
 * offset rather than inserting a fake node is what keeps this function about runs.
 */
export function layout(roots, colOffset = 0) {
    const out = [];
    let level = [...roots];
    let depth = 0;
    while (level.length) {
        level.forEach((node, i) => {
            out.push({ node, x: PAD + (depth + colOffset) * (CARD_W + GAP_X), y: PAD + i * (CARD_H + GAP_Y) });
        });
        level = level.flatMap((n) => n.children);
        depth++;
    }
    return out;
}
/** Where the origin card sits: the first column, centred against the column of roots it feeds. */
export function originAt(rootCount) {
    return { x: PAD, y: PAD + (Math.max(1, rootCount) - 1) * 0.5 * (CARD_H + GAP_Y) };
}
//# sourceMappingURL=run-pipes-view.js.map